import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Info,
  // Activity,
  Columns,
  Plus,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';

import { useTenant } from '@/hooks';
import {
  useCreateProjectTaskMutation,
  useAssignTaskToUserMutation,
  useDeleteProjectTaskMutation,
  useDuplicateProjectTaskMutation,
  useDuplicatedProjectMilestoneMutation,
  useGetCurrentProjectMembersQuery,
  useGetInternalProjectsDetailedResourcesQuery,
  useUpdateProjectTaskMutation,
  useGetExternalProjectMilestonesQuery,
  useCreateExternalTaskMutation,
  useUpdateExternalTaskMutation,
  useDeleteExternalTaskMutation,
  useRemoveTaskFromUserMutation,
} from '@/state';
import { cn } from '@/lib/utils';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

// import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MilestoneSelector } from './milestone-selector';
import { KanbanBoard } from './kanban-board';
import { MilestoneOverview } from './milestones-overview';
// import { ActivityTimeline } from './activity-timeline';

import { MilestoneUpsertDialog } from './create-milestones';
import { TaskDetailsDialog } from './task-details-dialog';
import { TaskUpsertDialog, type TaskUpsertPayload } from './task-upsert-dialog';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import type {
  // ActivityItem,
  MilestoneEditorValue,
  MilestoneItem,
  MilestoneRaw,
  ProjectDetailed,
  TaskRaw,
  ProjectTask,
  TaskCardProps,
  ProjectResponse,
  ApiErrorResponse,
} from '@/types';
import { toast } from 'sonner';
import {
  countsFromTasks,
  getTaskTitle,
  headerMetricLabel,
  normalizeTaskStatus,
  progressFromCounts,
} from '@/lib';
import UnderlinedTabs from '@/components/tabs';
import { AxiosError } from 'axios';

interface UpsertData {
  tasks: string;
  task_description: string;
  estimated_hours: number;
  priority: 'low' | 'medium' | 'high';
  due_date: Date | string;
  assignee_name?: string | null;
  status: 1 | 2 | 3;
}

// ============================================================================
// UI SHELL
// ============================================================================

function PageShell({
  title,
  subtitle,
  right,
  children,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-[2rem] border border-border/40 relative overflow-hidden bg-card/30 backdrop-blur-xl'
      )}
    >
      <div className="relative p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-2 text-sm sm:text-base text-muted-foreground/80 font-medium max-w-2xl">
                {subtitle}
              </p>
            ) : null}
          </div>
          {right ? (
            <div className="shrink-0 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
              {right}
            </div>
          ) : null}
        </div>

        <div className="h-px w-full bg-border my-8" />

        <div className="relative z-10">{children}</div>
      </div>
    </div>
  );
}

function LoadingNice() {
  return (
    <div className="grid grid-cols-12 gap-4">
      <Card className="col-span-12 lg:col-span-4 rounded-3xl">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-56" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-14 w-full rounded-2xl" />
          <Skeleton className="h-14 w-full rounded-2xl" />
          <Skeleton className="h-14 w-full rounded-2xl" />
          <Skeleton className="h-14 w-full rounded-2xl" />
        </CardContent>
      </Card>

      <Card className="col-span-12 lg:col-span-8 rounded-3xl">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full rounded-2xl" />
          <Skeleton className="h-72 w-full rounded-2xl" />
        </CardContent>
      </Card>
    </div>
  );
}

function EmptyNice({ text }: { text: string }) {
  return (
    <Card className="rounded-3xl border-border/40 bg-card/40 backdrop-blur-md h-full">
      <CardContent className="py-12 text-center">
        <div className="mx-auto max-w-md space-y-2">
          <div className="text-sm font-semibold text-foreground/70">{text}</div>
          <div className="text-xs text-muted-foreground/60">
            Add milestones and tasks to see the board here.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function HeaderStats({
  counts,
}: {
  counts: { todo: number; doing: number; done: number };
}) {
  const items: Array<{
    k: 'todo' | 'doing' | 'done';
    v: number;
    color: string;
    glow: string;
  }> = [
    {
      k: 'todo',
      v: counts.todo,
      color:
        'border-slate-200 text-slate-600 dark:border-slate-800 dark:text-slate-400',
      glow: '',
    },
    {
      k: 'doing',
      v: counts.doing,
      color: 'border-sky-500/30 text-sky-600 dark:text-sky-400',
      glow: 'shadow-[0_0_15px_-3px_rgba(14,165,233,0.3)]',
    },
    {
      k: 'done',
      v: counts.done,
      color: 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
      glow: 'shadow-[0_0_15px_-3px_rgba(16,185,129,0.3)]',
    },
  ];

  return (
    <div className="flex items-center gap-3">
      {items.map((x) => (
        <Badge
          key={x.k}
          variant="outline"
          className={cn(
            'rounded-full px-4 py-1.5 text-xs font-semibold transition-all duration-300 hover:border-foreground/20',
            x.color,
            x.glow
          )}
        >
          <span className="opacity-70 mr-1.5  tracking-wider text-[10px]">
            {headerMetricLabel(x.k)}
          </span>
          <span className="text-sm font-bold">{x.v}</span>
        </Badge>
      ))}
    </div>
  );
}

// ============================================================================
// MAIN
// ============================================================================

export const KanbanMilestones = ({
  projectId,
  projectDetails: preloadedProjectDetails,
  projectPk: passedProjectPk,
  isReadOnly = false,
}: {
  /**
   * IMPORTANT:
   * Pass `projectId` as the detailed-resource id (e.g. 75),
   * because your query expects `{ id }`.
   */
  projectId?: number;
  projectDetails?: ProjectResponse;
  projectPk?: number;
  isReadOnly?: boolean;
}) => {
  const { tenant } = useTenant();
  const [searchParams] = useSearchParams();
  const isExternal =
    searchParams.get('type') === 'EXTERNAL' ||
    preloadedProjectDetails?.project_type === 2;

  const {
    data: projectDetailed,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useGetInternalProjectsDetailedResourcesQuery(
    { tenant: tenant!, id: projectId! },
    {
      skip:
        !tenant ||
        !projectId ||
        !!preloadedProjectDetails?.project_management_project ||
        isExternal,
    }
  );

  const [duplicateMilestone] = useDuplicatedProjectMilestoneMutation();
  const [createTask] = useCreateProjectTaskMutation();
  const [updateTask] = useUpdateProjectTaskMutation();
  const [assignTaskToUser] = useAssignTaskToUserMutation();
  const [removeTaskFromUser] = useRemoveTaskFromUserMutation();
  const [deleteProjectTask] = useDeleteProjectTaskMutation();
  const [duplicateProjectTask] = useDuplicateProjectTaskMutation();

  const [createExternalTask] = useCreateExternalTaskMutation();
  const [updateExternalTask] = useUpdateExternalTaskMutation();
  const [deleteExternalTask] = useDeleteExternalTaskMutation();

  const [viewingTaskId, setViewingTaskId] = React.useState<number | null>(null);
  const [upsertOpen, setUpsertOpen] = React.useState(false);
  const [editingTask, setEditingTask] = React.useState<TaskRaw | null>(null);
  const [defaultStatus, setDefaultStatus] = React.useState<
    'todo' | 'doing' | 'done'
  >('todo');

  const typed = (preloadedProjectDetails || projectDetailed) as unknown as
    | ProjectDetailed
    | undefined;
  const realProjectId =
    passedProjectPk ||
    preloadedProjectDetails?.project_id ||
    typed?.project?.id;

  const { data: currentProjectMembers = [] } = useGetCurrentProjectMembersQuery(
    { tenant: tenant || '', projectId: realProjectId || 0 },
    { skip: !tenant || !realProjectId || isExternal }
  );

  const {
    data: externalMilestones,
    isLoading: isExternalLoading,
    isError: isExternalError,
    error: externalError,
    isFetching: isExternalFetching,
  } = useGetExternalProjectMilestonesQuery(
    { projectId: (isExternal ? projectId : realProjectId) || realProjectId! },
    { skip: !isExternal || (!projectId && !realProjectId) }
  );

  const finalLoading = isExternal ? isExternalLoading : isLoading;
  const finalError = isExternal ? isExternalError : isError;
  const finalErrorResponse = isExternal ? externalError : error;
  const finalFetching = isExternal ? isExternalFetching : isFetching;

  const milestonesRaw: MilestoneRaw[] = React.useMemo(() => {
    if (isExternal) {
      return externalMilestones ?? [];
    }
    // If we have preloaded data, return it
    if (preloadedProjectDetails?.project_management_project) {
      return preloadedProjectDetails.project_management_project;
    }
    // Otherwise fallback to the query result
    return typed?.project?.project_management_project ?? [];
  }, [
    isExternal,
    externalMilestones,
    preloadedProjectDetails?.project_management_project,
    typed?.project?.project_management_project,
  ]);

  const milestones: MilestoneItem[] = React.useMemo(() => {
    return milestonesRaw
      .slice()
      .sort((a, b) => (a.milestone_order ?? 999) - (b.milestone_order ?? 999))
      .map((m) => {
        const counts = countsFromTasks(m.tasks_milestones ?? []);
        const progressPct = progressFromCounts(counts);

        return {
          key: `m-${m.id}`,
          title: m.milestones,
          description: m.milestone_description ?? undefined,
          progressPct,
          counts,
          milestoneId: m.id,
        };
      });
  }, [milestonesRaw]);

  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!selectedKey && milestones.length) setSelectedKey(milestones[0].key);
  }, [milestones, selectedKey]);

  const selected = React.useMemo(() => {
    return milestones.find((m) => m.key === selectedKey) ?? milestones[0];
  }, [milestones, selectedKey]);

  const selectedMilestoneData = React.useMemo(() => {
    const id = selected?.milestoneId as number | undefined;
    if (!id) return null;
    return milestonesRaw.find((m) => m.id === id) ?? null;
  }, [milestonesRaw, selected]);

  const selectedTasks: TaskRaw[] = React.useMemo(() => {
    return selectedMilestoneData?.tasks_milestones ?? [];
  }, [selectedMilestoneData]);

  const sourceMembers =
    isExternal && Array.isArray(typed?.project?.project)
      ? (typed.project.project as any[])
      : currentProjectMembers;

  const projectAssigneeOptions = sourceMembers.map((member: any) => ({
    id: String(member.id ?? member.candidate ?? ''),
    name:
      [
        member.first_name ?? member.candidate_first_name,
        member.last_name ?? member.candidate_last_name,
      ]
        .filter(Boolean)
        .join(' ')
        .trim() ||
      member.email ||
      member.candidate_email ||
      'User',
    email: member.email || member.candidate_email || '',
    photo_url: member.photo_url ?? member.profile?.photo_url ?? null,
    schema_id: Number(member.schema_id ?? 0),
    schema_name: String(member.schema_name ?? tenant ?? ''),
  }));

  const taskCards: TaskCardProps[] = React.useMemo(() => {
    return selectedTasks.map((t) => ({
      id: t.id,
      taskUuid:
        (t as any).tasks_uuid ?? (t as any).task_uuid ?? (t as any).uuid,
      title: getTaskTitle(t),
      description: t.description || t.task_description,
      status: normalizeTaskStatus(t),
      priority: (t.priority as 'low' | 'medium' | 'high') || undefined,
      dueDate: t.due_date || undefined,
      assigneeName: t.assignee_name,
      estimatedHours: t.estimated_hours,
      actualTime: t.actual_time,
      documents: t.documents,
      assignees: Array.isArray(t.assigned_to)
        ? t.assigned_to.filter(Boolean).map((user: any) => ({
            id: String(user.id ?? user.candidate ?? user.user_id ?? ''),
            name:
              [user.first_name, user.last_name]
                .filter(Boolean)
                .join(' ')
                .trim() ||
              user.name ||
              user.email ||
              'User',
            email: user.email ?? '',
            photo_url: user.photo_url ?? user.profile?.photo_url ?? null,
            schema_id: Number(user.schema_id ?? 0),
            schema_name: String(user.schema_name ?? tenant ?? ''),
          }))
        : [],
      assigneeOptions: projectAssigneeOptions,
      selectedAssigneeIds: Array.isArray(t.assigned_to)
        ? t.assigned_to
            .filter(Boolean)
            .map((user: any) =>
              String(user.id ?? user.candidate ?? user.user_id ?? '')
            )
            .filter(Boolean)
        : [],
    }));
  }, [projectAssigneeOptions, selectedTasks, tenant]);

  const handleAssigneeToggle = React.useCallback(
    async ({
      taskId,
      taskUuid,
      assignee,
      selected,
    }: {
      taskId: number;
      taskUuid?: string;
      assignee: {
        id: string;
        name: string;
        photo_url?: string | null;
        schema_id: number;
        schema_name: string;
      };
      selected: boolean;
    }) => {
      if (!tenant || !taskUuid) {
        toast.error('Task identifier missing. Please refresh and try again.');
        return;
      }

      try {
        if (selected) {
          await removeTaskFromUser({
            tenant,
            data: {
              task_uuid: taskUuid,
              assigned_to: assignee.id,
              task: taskId,
              task_id: taskId,
              schema_id: assignee.schema_id,
              schema_name: assignee.schema_name || tenant,
            },
          }).unwrap();
          toast.success('User removed from task');
        } else {
          await assignTaskToUser({
            tenant,
            data: {
              task_uuid: taskUuid,
              assigned_to: assignee.id,
              task: taskId,
              schema_id: assignee.schema_id,
              schema_name: assignee.schema_name || tenant,
            },
          }).unwrap();
          toast.success('User assigned to task');
        }
      } catch (err) {
        console.error('[handleAssigneeToggle]', {
          taskId,
          taskUuid,
          assignee: assignee.id,
          selected,
          error: err,
        });
        toast.error(
          selected ? 'Failed to remove user' : 'Failed to assign user'
        );
      }
    },
    [assignTaskToUser, removeTaskFromUser, tenant]
  );

  const board = React.useMemo(() => {
    return {
      todo: taskCards.filter((t) => t.status === 'todo'),
      doing: taskCards.filter((t) => t.status === 'doing'),
      done: taskCards.filter((t) => t.status === 'done'),
    };
  }, [taskCards]);

  // const activityItems: ActivityItem[] = React.useMemo(() => {
  //   return selectedTasks
  //     .slice()
  //     .sort((a, b) => {
  //       const ta = new Date(a.updated_on || a.created_on || 0).getTime();
  //       const tb = new Date(b.updated_on || b.created_on || 0).getTime();
  //       return tb - ta;
  //     })
  //     .slice(0, 12)
  //     .map((t) => ({
  //       id: t.id,
  //       title: getTaskTitle(t),
  //       description: t.description,
  //       status: normalizeTaskStatus(t),
  //       timestamp: t.updated_on || t.created_on || new Date().toISOString(),
  //     }));
  // }, [selectedTasks]);

  // ========================================================================
  // CREATE + EDIT STATE
  // ========================================================================

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<MilestoneEditorValue | null>(
    null
  );
  const [showMilestones, setShowMilestones] = React.useState(true);

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (milestoneId: number) => {
    const m = milestonesRaw.find((x) => x.id === milestoneId);
    if (!m) return;

    setEditing({
      id: m.id,
      milestones: m.milestones,
      milestone_description: m.milestone_description,
      end_date: m.end_date,
    });
    setEditorOpen(true);
  };

  const onDuplicateMilestone = React.useCallback(
    async (milestoneId: number) => {
      try {
        await duplicateMilestone({
          tenant: tenant!,
          old_id: milestoneId,
        }).unwrap();
        toast.success('Milestone duplicated');
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          toast.error(
            err.response?.data?.message ?? 'Failed to duplicate milestone'
          );
        } else {
          toast.error('Failed to duplicate milestone');
        }
        throw err;
      }
    },
    [tenant, duplicateMilestone]
  );

  const handleAddTask = (
    payloadOrStatus: 'todo' | 'doing' | 'done' | TaskUpsertPayload
  ) => {
    if (typeof payloadOrStatus === 'string') {
      setDefaultStatus(payloadOrStatus as 'todo' | 'doing' | 'done');
      setEditingTask(null);
      setUpsertOpen(true);
      return;
    }
    // Backward compatibility for KanbanColumn's internal dialog if still used
    setDefaultStatus(payloadOrStatus.status || 'todo');
    setEditingTask(null);
    setUpsertOpen(true);
  };

  const handleUpsertSubmit = async (payload: TaskUpsertPayload) => {
    if ((!isExternal && !tenant) || !selected?.milestoneId) return;

    try {
      if (payload.id) {
        const statusMap: Record<string, number> = {
          todo: 1,
          doing: 2,
          done: 3,
        };
        // Update
        if (isExternal) {
          await updateExternalTask({
            id: payload.id,
            data: {
              tasks: payload.title,
              task_description: payload.description,
              estimated_hours: payload.estimated_hours,
              status: statusMap[payload.status] as 1 | 2 | 3,
            },
          }).unwrap();
        } else {
          const updateData: UpsertData = {
            tasks: payload.title,
            task_description: payload.description,
            estimated_hours: payload.estimated_hours,
            priority: payload.priority as 'low' | 'medium' | 'high',
            due_date: payload.due_date as string,
            assignee_name: payload.assignee_name,
            status: statusMap[payload.status] as 1 | 2 | 3,
          };
          await updateTask({
            tenant: tenant!,
            taskId: payload.id,
            data: updateData,
          }).unwrap();
        }
        toast.success('Task updated');
      } else {
        // Create
        if (isExternal) {
          await createExternalTask({
            data: {
              tasks: payload.title,
              task_description: payload.description,
              estimated_hours: payload.estimated_hours,
              project_milestones: selected.milestoneId,
            },
          }).unwrap();
        } else {
          await createTask({
            tenant: tenant!,
            data: {
              tasks: payload.title,
              task_description: payload.description,
              estimated_hours: payload.estimated_hours,
              project_milestones: selected.milestoneId,
            },
          }).unwrap();
        }
        toast.success(`Task "${payload.title}" created`);
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.message ?? 'Failed to save task');
      } else {
        toast.error('Failed to save task');
      }
      throw err;
    }
  };

  const handleTaskDrop = React.useCallback(
    async (taskId: number, newStatus: string) => {
      const statusMap: Record<string, number> = {
        todo: 1,
        doing: 2,
        done: 3,
      };

      const numericStatus = statusMap[newStatus];
      if (!numericStatus) return;

      try {
        if (isExternal) {
          await updateExternalTask({
            id: taskId,
            data: { status: numericStatus },
          }).unwrap();
        } else {
          await updateTask({
            tenant: tenant!,
            taskId,
            data: { status: numericStatus },
          }).unwrap();
        }
        toast.success(`Task moved to ${newStatus}`);
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          toast.error(err.response?.data?.message ?? 'Failed to move task');
        } else {
          toast.error('Failed to move task');
        }
        throw err;
      }
    },
    [tenant, isExternal, updateExternalTask, updateTask]
  );

  const handleDeleteTask = React.useCallback(
    async (taskId: number) => {
      try {
        if (isExternal) {
          await deleteExternalTask({ id: taskId }).unwrap();
        } else {
          await deleteProjectTask({ tenant: tenant!, taskId }).unwrap();
        }
        toast.success('Task deleted successfully');
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          toast.error(err.response?.data?.message ?? 'Failed to delete task');
        } else {
          toast.error('Failed to delete task');
        }
        throw err;
      }
    },
    [deleteProjectTask, isExternal, deleteExternalTask, tenant]
  );

  const handleDuplicateTask = React.useCallback(
    async (taskId: number) => {
      try {
        await duplicateProjectTask({ tenant: tenant!, taskId }).unwrap();
        toast.success('Task duplicated');
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          toast.error(
            err.response?.data?.message ?? 'Failed to duplicate task'
          );
        } else {
          toast.error('Failed to duplicate task');
        }
        throw err;
      }
    },
    [duplicateProjectTask, tenant]
  );

  const handleEditTask = (taskId: number) => {
    const task = selectedTasks.find((t) => t.id === taskId);
    if (!task) return;
    setEditingTask(task);
    setUpsertOpen(true);
  };

  const handleTaskClick = (taskId: number) => {
    setViewingTaskId(taskId);
  };

  const viewingTask = React.useMemo(() => {
    if (!viewingTaskId) return null;
    return (
      (selectedTasks.find(
        (t) => t.id === viewingTaskId
      ) as unknown as ProjectTask) || null
    );
  }, [viewingTaskId, selectedTasks]);

  // Tabs
  const tabs = [
    {
      value: 'board',
      title: 'Board',
      icon: <Columns className="w-4 h-4 mr-2" />,
      content: (
        <KanbanBoard
          tasks={board}
          onTaskClick={handleTaskClick}
          onAddClick={isReadOnly ? undefined : handleAddTask}
          onTaskDrop={isReadOnly ? undefined : handleTaskDrop}
          onDeleteTask={isReadOnly ? undefined : handleDeleteTask}
          onDuplicateTask={isReadOnly ? undefined : handleDuplicateTask}
          onEditTask={isReadOnly ? undefined : handleEditTask}
          onAssigneeToggle={isReadOnly ? undefined : handleAssigneeToggle}
        />
      ),
    },
    {
      value: ' info-and-docs',
      title: ' Info & Docs',
      icon: <Info className="w-4 h-4 mr-2" />,
      content: (
        <MilestoneOverview
          title={selected?.title}
          description={
            selectedMilestoneData?.milestone_description ?? undefined
          }
          progressPct={selected?.progressPct}
          counts={selected?.counts}
          milestoneId={selected?.milestoneId}
          isExternal={isExternal}
        />
      ),
    },
  ];

  // ========================================================================
  // STATES
  // ========================================================================

  if (!tenant || !projectId) {
    return (
      <PageShell
        title="Milestones"
        subtitle="Select a project to view milestones."
      >
        <EmptyNice text="No project selected." />
      </PageShell>
    );
  }

  if (finalError) {
    return (
      <PageShell title="Milestones" subtitle="Something went wrong.">
        <Card className="rounded-3xl">
          <CardContent className="py-10 text-center">
            <div className="text-base font-medium">
              Failed to load project data.
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Check the API response and query params.
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (finalLoading) {
    return (
      <PageShell title="Milestones" subtitle="Loading project milestones…">
        <LoadingNice />
      </PageShell>
    );
  }

  if (!typed) {
    return (
      <PageShell title="Milestones" subtitle="Project not found.">
        <EmptyNice text="Project not found." />
      </PageShell>
    );
  }

  // ========================================================================
  // UI
  // ========================================================================

  const headerCounts = selected?.counts ?? { todo: 0, doing: 0, done: 0 };

  return (
    <PageShell
      title="Task Progress"
      subtitle="Milestones → Overview → Activity → Kanban"
      right={
        <div className="flex items-center gap-2">
          <HeaderStats counts={headerCounts} />
        </div>
      }
    >
      <div className="grid grid-cols-12 gap-4">
        {/* LEFT */}
        {showMilestones && (
          <div className="col-span-12 lg:col-span-4 border border-border/40 rounded-3xl overflow-hidden animate-in fade-in slide-in-from-left-4 duration-300 bg-card/40 backdrop-blur-xl shadow-sm">
            <Card className="h-full border-0 bg-transparent shadow-none">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowMilestones(false)}
                      className="h-8 w-8 rounded-full hover:bg-muted"
                      title="Collapse Sidebar"
                    >
                      <PanelLeftClose className="h-4 w-4" />
                    </Button>
                    <div className="min-w-0">
                      <CardTitle className="text-base text-card-foreground">
                        Milestones
                      </CardTitle>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className="rounded-full px-2 py-0 h-5 text-[10px]"
                    >
                      {milestones.length}
                    </Badge>

                    {!isReadOnly && (
                      <Button onClick={openCreate}>
                        <span className="hidden md:inline-block">New</span>
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <WithLoadingErrorWrapper
                  error={finalErrorResponse as ApiErrorResponse}
                  loading={finalLoading}
                  isFetching={finalFetching}
                  onRetry={refetch}
                >
                  <MilestoneSelector
                    milestones={milestones}
                    selectedKey={selectedKey}
                    onSelect={setSelectedKey}
                    loading={false}
                    onEditMilestone={isReadOnly ? undefined : openEdit}
                    onDuplicateMilestone={
                      isReadOnly ? undefined : onDuplicateMilestone
                    }
                    isExternal={isExternal}
                  />
                </WithLoadingErrorWrapper>
              </CardContent>
            </Card>
          </div>
        )}

        {/* RIGHT */}
        <div
          className={cn(
            'col-span-12 border border-border/40 bg-card/40 backdrop-blur-xl rounded-3xl transition-all duration-300 relative shadow-sm',
            showMilestones ? 'lg:col-span-8' : 'lg:col-span-12'
          )}
        >
          {!showMilestones && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowMilestones(true)}
              className="absolute -left-4 top-6 z-20 h-8 w-8 rounded-full bg-background shadow-md lg:flex hidden"
              title="Expand Sidebar"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          )}
          {!selected ? (
            <EmptyNice text="Select a milestone on the left." />
          ) : (
            <Card className="p-7 bg-transparent">
              <UnderlinedTabs tabItems={tabs} />
            </Card>
          )}
        </div>
      </div>

      {/* Create / Edit dialog */}
      {realProjectId ? (
        <MilestoneUpsertDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          projectId={(isExternal ? projectId : realProjectId) || realProjectId!}
          initial={editing}
          isExternal={isExternal}
        />
      ) : null}

      <TaskDetailsDialog
        task={viewingTask}
        open={!!viewingTaskId}
        onOpenChange={(open) => !open && setViewingTaskId(null)}
        isExternal={isExternal}
        assigneeOptions={projectAssigneeOptions}
        selectedAssigneeIds={
          Array.isArray(viewingTask?.assigned_to)
            ? viewingTask.assigned_to
                .filter(Boolean)
                .map((user: any) =>
                  String(user.id ?? user.candidate ?? user.user_id ?? '')
                )
                .filter(Boolean)
            : []
        }
        onAssigneeToggle={handleAssigneeToggle}
      />

      <TaskUpsertDialog
        open={upsertOpen}
        onOpenChange={setUpsertOpen}
        onSubmit={handleUpsertSubmit}
        initialData={editingTask}
        defaultStatus={defaultStatus}
        isExternal={isExternal}
      />
    </PageShell>
  );
};
